import { useMemo } from 'react';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { UserRakePayout } from '@/services/rake/rake.types';
import { formatCurrency } from '@/pages/table/utils/utils';
import { LiaSortSolid, LiaSortUpSolid, LiaSortDownSolid } from 'react-icons/lia';
import CurrencyIcon from '@/components/CurrencySymbolMask/CurrencyIcon';

interface ReferralsTableProps {
  referredUsers: UserRakePayout[];
  maxHeight?: number;
}

const columnHelper = createColumnHelper<UserRakePayout>();

const ReferralsTable = ({ referredUsers, maxHeight }: ReferralsTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor('user.firstName', {
        enableSorting: true,
        header: 'Name',
        cell: ({ row }) => {
          return (
            <div className="flex flex-col items-start justify-start gap-1 pl-4">
              <div className="flex items-center justify-start gap-1">
                <p className="text-sm font-semibold text-ph-gray-silver">
                  {row.original.user.firstName} {row.original.user.lastName}
                </p>
                {row.original.user.isAgent && (
                  <span className="rounded-md bg-ph-primary-violet px-1 py-[1px] text-xs font-normal text-ph-gray-white">
                    Agent
                  </span>
                )}
              </div>
              <span className="text-xs font-normal text-ph-gray-cod-light">{row.original.user.email}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor('user.createdAt', {
        enableSorting: true,
        header: 'Date Joined',
        cell: ({ getValue }) => (
          <div className="flex flex-col items-start justify-start gap-1">
            <p className="text-sm font-semibold text-ph-gray-silver">{format(new Date(getValue()), 'MMM dd, yyyy')}</p>
          </div>
        ),
      }),
      columnHelper.accessor('lastPayout', {
        enableSorting: true,
        header: 'Rakeshare End Date',
        cell: ({ getValue, row }) => {
          const lastPayout = getValue();
          const dateToFormat = lastPayout ? new Date(lastPayout) : new Date(row.original.user.createdAt);
          return (
            <div className="flex flex-col items-start justify-start gap-1 pl-4">
              <p className="text-sm font-semibold text-ph-gray-silver">{format(dateToFormat, 'MMM dd, yyyy')}</p>
            </div>
          );
        },
      }),
      columnHelper.accessor('amount', {
        enableSorting: true,
        header: 'Total Rake',
        cell: ({ getValue }) => (
          <div className="flex flex-col items-start justify-start gap-1 pl-4">
            <span className="flex flex-row items-center text-sm font-semibold text-ph-gray-silver">
              <CurrencyIcon />
              <span className="ml-1">{formatCurrency(getValue())}</span>
            </span>
          </div>
        ),
      }),
    ],
    [],
  );

  const tableInstance = useReactTable<UserRakePayout>({
    columns,
    data: referredUsers,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className={twMerge('scrollbar-phenom block w-full overflow-auto', maxHeight && `max-h-[${maxHeight}px]`)}>
      <table className="w-full table-auto">
        <thead>
          <tr>
            {tableInstance.getFlatHeaders().map((header) => {
              const canHeaderSort = header.column.getCanSort();

              return (
                <th
                  key={header.id}
                  className={twMerge(
                    'sticky top-0 h-10 select-none bg-pn-violet-medium py-2 text-left text-xs font-semibold text-ph-gray-dark first:rounded-l-xl first:pl-5 last:rounded-r-xl hover:cursor-pointer hover:text-ph-gray-silver',
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className="flex items-center justify-start gap-1">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span>
                      {
                        {
                          asc: <LiaSortUpSolid />,
                          desc: <LiaSortDownSolid />,
                        }[(header.column.getIsSorted() as string) ?? null]
                      }
                      {canHeaderSort && !header.column.getIsSorted() && <LiaSortSolid />}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {tableInstance.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={twMerge(
                'h-16 py-2 text-left text-sm font-semibold text-ph-gray-silver hover:bg-pn-violet-medium',
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ReferralsTable;
